import React, { useEffect, useState } from "react";
import { Camera } from "lucide-react";

/* Le rond d'un élève : sa photo, son avatar ou son initiale (Jen, 02.11.2026 : « est-ce que
   la famille voudrait mettre une photo sur sa tuile, en cliquant sur la lettre, plutôt que
   d'avoir une lettre ? »). Partagé par l'app des familles, la console et l'app école.

   La liste fermée des avatars : le serveur refuse tout le reste (console/photos_eleves.py,
   AVATARS — un test vérifie que les deux listes sont les mêmes). */
export const AVATARS = [
  { id: "danseuse", emoji: "💃", fond: "#FCE7F3" },
  { id: "danseur", emoji: "🕺", fond: "#EDE9FE" },
  { id: "chaussons", emoji: "🩰", fond: "#FFE4E6" },
  { id: "micro", emoji: "🎤", fond: "#E0F2FE" },
  { id: "theatre", emoji: "🎭", fond: "#FEF3C7" },
  { id: "guitare", emoji: "🎸", fond: "#FFEDD5" },
  { id: "etoile", emoji: "🌟", fond: "#FEF9C3" },
  { id: "licorne", emoji: "🦄", fond: "#F3E8FF" },
  { id: "renard", emoji: "🦊", fond: "#FFEDD5" },
  { id: "panda", emoji: "🐼", fond: "#E2E8F0" },
  { id: "chat", emoji: "🐱", fond: "#FEF3C7" },
  { id: "chien", emoji: "🐶", fond: "#DCFCE7" },
];

export const avatarDe = (id) => AVATARS.find((a) => a.id === id) || null;

/* Le rond lui-même. `photo` : l'adresse de l'image (blob), sinon `avatar`, sinon l'initiale
   sur `teinte`. Avec `onClick`, c'est un bouton, marqué d'un petit appareil photo : on voit
   qu'on peut le toucher, sans que ça prenne la place du prénom. */
export const RondEleve = ({ prenom = "", photo = "", avatar = "", teinte = "var(--primary)", taille = 44,
  onClick, testId, label }) => {
  const a = !photo ? avatarDe(avatar) : null;
  const rond = (
    <span className="rounded-full overflow-hidden grid place-items-center text-white font-semibold select-none"
      style={{ width: taille, height: taille, background: photo ? "#E2E8F0" : a ? a.fond : teinte,
        fontSize: a ? Math.round(taille * 0.56) : Math.round(taille * 0.4), lineHeight: 1 }}
      data-rond={photo ? "photo" : a ? "avatar" : "lettre"}>
      {photo ? <img src={photo} alt="" className="w-full h-full object-cover" draggable={false} />
        : a ? <span aria-hidden="true">{a.emoji}</span> : (prenom || "?")[0]}
    </span>
  );
  if (!onClick) return <span className="relative inline-flex shrink-0" data-testid={testId}>{rond}</span>;
  const badge = Math.max(18, Math.round(taille * 0.4));
  return (
    <button type="button" onClick={onClick} aria-label={label || `Photo ou avatar de ${prenom}`}
      className="relative inline-flex shrink-0 rounded-full active:scale-95 transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--primary)]"
      data-testid={testId}>
      {rond}
      <span className="absolute grid place-items-center rounded-full bg-white"
        style={{ width: badge, height: badge, right: -3, bottom: -3, color: "#64748B",
          boxShadow: "0 1px 3px rgba(15,27,45,0.22)" }}
        data-testid={testId ? `${testId}-badge` : undefined} aria-hidden="true">
        <Camera size={Math.round(badge * 0.58)} strokeWidth={2.2} />
      </span>
    </button>
  );
};

/* Les photos se lisent à part (/enfant/{id}/photo, /eleves/{id}/photo) : une seule fois par
   version et par visite, puis gardées ici. `charger(id, v)` rend l'adresse d'un blob. */
export function creerPhotos(charger) {
  const memoire = new Map();   // « id@version » → promesse de l'adresse
  const pretes = new Map();    // « id@version » → adresse déjà là (pas de lettre qui clignote)
  const retenir = (id, v, url) => {
    const cle = `${id}@${v}`;
    pretes.set(cle, url);
    memoire.set(cle, Promise.resolve(url));
  };
  const usePhoto = (id, v) => {
    const cle = `${id}@${v}`;
    const [url, setUrl] = useState(() => (id && v ? pretes.get(cle) || "" : ""));
    useEffect(() => {
      if (!id || !v) { setUrl(""); return undefined; }
      if (!memoire.has(cle)) {
        memoire.set(cle, charger(id, v)
          .then((u) => { pretes.set(cle, u); return u; })
          // Pas d'image (réseau, copie de test) : la lettre reste ; on réessaiera plus tard.
          .catch(() => { memoire.delete(cle); return ""; }));
      }
      let vivant = true;
      memoire.get(cle).then((u) => { if (vivant) setUrl(u); });
      return () => { vivant = false; };
    }, [id, v, cle]);
    return url;
  };
  return { usePhoto, retenir };
}
